import { useQuery } from '@tanstack/react-query';
import { SuperSearchResponse, SuperSearchResult, SuperSearchService } from 'api';

const SUPER_SEARCH_QUERY_KEY = 'super-search';
const SUPER_SEARCH_AUTHORIZATION_CACHE_SCOPE = 'authorization-v1';
const SUPER_SEARCH_RESULT_LIMIT = 100;

interface SuperSearchQueryOptions {
  contextCompanyId?: number;
  contextFirmId?: number;
  contextMeasurementDate?: string;
  includeSavedDestinations?: boolean;
  includeDocuments?: boolean;
  requestKey?: string;
}

const isStableNavigationResult = (result: SuperSearchResult) =>
  result.type === SuperSearchResult.type.FIRM ||
  result.type === SuperSearchResult.type.COMPANY ||
  result.type === SuperSearchResult.type.FIRM_PAGE ||
  result.type === SuperSearchResult.type.COMPANY_PAGE;

const useSuperSearchQuery = (
  query: string,
  enabled: boolean,
  {
    contextCompanyId,
    contextFirmId,
    contextMeasurementDate,
    includeSavedDestinations = false,
    includeDocuments = false,
    requestKey = query,
  }: SuperSearchQueryOptions = {}
) => {
  const isLiveSearch = includeSavedDestinations || includeDocuments;

  return useQuery<SuperSearchResponse>({
    enabled,
    meta: { persist: false, showGlobalError: false },
    placeholderData: isLiveSearch
      ? previousData =>
        previousData
          ? {
            results: previousData.results.filter(isStableNavigationResult),
          }
          : undefined
      : previousData => previousData,
    queryFn: ({ signal }) => {
      const request = SuperSearchService.apiSuperSearchList(
        query || undefined,
        SUPER_SEARCH_RESULT_LIMIT,
        includeDocuments,
        includeSavedDestinations,
        contextFirmId,
        contextCompanyId,
        contextMeasurementDate
      );
      const cancelRequest = () => request.cancel();

      if (signal.aborted) {
        cancelRequest();
      } else {
        signal.addEventListener('abort', cancelRequest, { once: true });
      }

      return request.finally(() => signal.removeEventListener('abort', cancelRequest));
    },
    queryKey: [
      SUPER_SEARCH_QUERY_KEY,
      SUPER_SEARCH_AUTHORIZATION_CACHE_SCOPE,
      requestKey,
      query,
      includeDocuments,
      includeSavedDestinations,
      contextFirmId,
      contextCompanyId,
      contextMeasurementDate,
    ],
    refetchOnMount: isLiveSearch ? 'always' : true,
    retry: false,
    staleTime: isLiveSearch ? 0 : 30_000,
  });
};

export default useSuperSearchQuery;
